import React, { useState, useEffect } from "react";
import {
  X,
  Search,
  CheckCircle2,
  Clock,
  Calendar,
  Users,
  Ticket,
  QrCode,
  AlertCircle,
  Copy,
  User,
  Mail,
  XCircle,
} from "lucide-react";
import { getBookingByCode, getBookingByEmail } from "../services/api/bookings";
import { BookingRecord } from "../types";

interface BookingCheckModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const BookingCheckModal: React.FC<BookingCheckModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [inputVal, setInputVal] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "found" | "error">(
    "idle"
  );
  const [ticketData, setTicketData] = useState<BookingRecord | null>(null);
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const lastCode = localStorage.getItem("giz_last_booking_code");
      if (lastCode) setInputVal(lastCode);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCheck = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputVal.trim()) return;
    setStatus("loading");
    try {
      let result: BookingRecord | null = null;
      if (inputVal.includes("@")) {
        result = await getBookingByEmail(inputVal.trim());
      } else {
        result = await getBookingByCode(inputVal.trim());
      }
      if (result) {
        setTicketData(result);
        setStatus("found");
        // Save the code for future reference
        localStorage.setItem("giz_last_booking_code", result.code);
      } else {
        setStatus("error");
      }
    } catch (error) {
      console.error("Failed to fetch booking:", error);
      setStatus("error");
    }
  };

  const resetSearch = () => {
    setStatus("idle");
    setInputVal("");
    setTicketData(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden relative flex flex-col max-h-[90vh]">
        <div className="bg-gray-900 p-4 flex justify-between items-center text-white shrink-0">
          <div className="flex items-center gap-2">
            <div className="bg-white/20 p-1.5 rounded-lg">
              <Ticket className="w-5 h-5" />
            </div>
            <h3 className="font-bold text-lg">Cek Pesanan</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto">
          {status !== "found" ? (
            <div className="space-y-6">
              <p className="text-gray-600 text-center">
                Masukkan <strong>Kode Booking</strong> atau{" "}
                <strong>Email</strong>.
              </p>
              <form onSubmit={handleCheck} className="relative">
                <input
                  type="text"
                  placeholder="GIZ-XXXX atau Email"
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-4 text-center font-mono text-lg font-bold bg-white text-gray-900 focus:border-giz-primary outline-none transition-colors"
                  value={inputVal}
                  onChange={(e) => setInputVal(e.target.value)}
                />
                {status === "error" && (
                  <p className="text-red-500 text-sm mt-2 text-center">
                    Data tidak ditemukan.
                  </p>
                )}
                <button
                  disabled={status === "loading"}
                  type="submit"
                  className="w-full mt-4 bg-giz-primary text-white font-bold py-4 rounded-xl shadow-lg"
                >
                  {status === "loading" ? "Mencari..." : "Cek Status"}
                </button>
              </form>
            </div>
          ) : (
            ticketData && (
              <div className="animate-slideUp">
                {(() => {
                  const statusLower = ticketData.status?.toLowerCase() || "";
                  const badgeClass =
                    statusLower === "completed"
                      ? "bg-slate-200 text-slate-700"
                      : statusLower === "paid"
                      ? "bg-green-100 text-green-700"
                      : "bg-orange-100 text-orange-700";
                  const badgeText =
                    statusLower === "completed"
                      ? "TIKET SUDAH DIGUNAKAN"
                      : statusLower === "paid"
                      ? "PEMBAYARAN LUNAS"
                      : "MENUNGGU PEMBAYARAN";

                  return (
                    <div
                      className={`text-center py-2 px-4 rounded-full font-bold text-sm mb-6 w-fit mx-auto ${badgeClass}`}
                    >
                      {badgeText}
                    </div>
                  );
                })()}
                <div className="border border-gray-200 rounded-2xl overflow-hidden shadow-sm relative">
                  <div className="h-2 bg-gradient-to-r from-giz-primary to-riw-primary"></div>
                  <div className="p-6 text-center">
                    <h2 className="text-3xl font-mono font-bold text-gray-900 mb-4">
                      {ticketData.code}
                    </h2>
                    {ticketData.status?.toLowerCase() !== "completed" ? (
                      <div className="bg-white p-2 border border-gray-100 rounded-xl inline-block mb-4">
                        <img
                          src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${ticketData.code}`}
                          alt="QR"
                          className="w-40 h-40 mix-blend-multiply"
                          loading="lazy"
                        />
                      </div>
                    ) : (
                      <div className="bg-amber-50 text-amber-700 border border-amber-100 rounded-xl px-4 py-3 inline-block mb-4 text-sm font-semibold">
                        Tiket telah digunakan.
                      </div>
                    )}
                    <div className="bg-gray-50 rounded-xl p-4 text-left space-y-2 text-sm">
                      <p>
                        <strong>Nama:</strong> {ticketData.customerName}
                      </p>
                      <p>
                        <strong>Tiket:</strong> {ticketData.ticketType}
                      </p>
                      <p>
                        <strong>Pax:</strong> {ticketData.pax} Orang
                      </p>
                    </div>
                  </div>
                </div>
                <button
                  onClick={resetSearch}
                  className="w-full mt-6 py-3 text-gray-500 font-bold"
                >
                  Cek Lainnya
                </button>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};
